/**
 * Credit gate rule inspect view per scope §6.6.2. Atlas Day 3a P6.
 *
 * D-1 lock 2026-05-27: credit_ledger actual schema (event_type + notes +
 * balance_after + po_id + amount + distributor_id + created_by + created_at).
 * L7 references distributors.provenance for credit_limit + payment_terms;
 * credit_ledger entries are their own trace via the immutable append-only
 * ledger per scope §6.6.2 L9.
 */

import type { JSX } from 'react';
import {
  URL_DIRECTORY_DISTRIBUTORS,
} from '../../../lib/atlas/atlasUrls';
import { CREDIT_GATE_PROPAGATION } from '../../../lib/atlas/propagationMaps';
import { FIELD_CLASSIFICATIONS } from '../../../lib/atlas/classificationMaps';
import InspectShell from './InspectShell';
import LayerSection from './LayerSection';
import ComposabilityReservedLayer from './ComposabilityReservedLayer';

const FINANCE_CREDIT_CASH_URL = 'https://arbi.ironcladsupply.co/finance/credit-cash';

export default function CreditGateInspect(): JSX.Element {
  const distFields = FIELD_CLASSIFICATIONS.distributors;

  return (
    <InspectShell
      subRegionName="Credit gate"
      surfaceLine="ISG extends credit to N of M distributors. Credit posture concentrated in K distributors."
    >
      <LayerSection layer={4} title="Math vs judgment">
        <p>
          Per scope §6.6.2: judgment-prominent for{' '}
          <code>distributor.credit_limit</code> + <code>distributor.payment_terms</code> (ISG&apos;s
          credit-extension choices); math component for current ledger state arithmetic per{' '}
          <code>credit_ledger</code> entries (balance_after = sum of preceding events).
        </p>
      </LayerSection>

      <LayerSection layer={5} title="Mechanical vs strategic">
        <p>
          Strategic for <code>distributor.credit_limit</code> + <code>distributor.payment_terms</code>{' '}
          (per FIELD_CLASSIFICATIONS.distributors: layer5Nature ={' '}
          {distFields.credit_limit.layer5Nature}). Mechanical for{' '}
          <code>credit_ledger</code> entry amounts (recorded transactional state — not stance).
        </p>
      </LayerSection>

      <LayerSection layer={6} title="Operational vs ownership">
        <p>
          Ownership-altitude for credit-extension stance: <code>credit_limit</code> +{' '}
          <code>payment_terms</code> (per FIELD_CLASSIFICATIONS.distributors: layer6Authority ={' '}
          {distFields.credit_limit.layer6Authority}). Ledger entries are transactional, not
          configuration-altitude — outside layer 6 per-value scope (region-altitude inspect:
          ledger represents historical credit-allocation events).
        </p>
      </LayerSection>

      <LayerSection layer={7} title="Trace">
        <p>
          Per scope §6.6.2 L7 — distributed substrate trace:
        </p>
        <ul className="list-disc pl-5 text-xs space-y-1">
          <li>
            <code>distributors.provenance</code> covers <code>credit_limit</code> +{' '}
            <code>payment_terms</code> changes (Day 67 P2.2 substrate)
          </li>
          <li>
            <code>credit_ledger</code> entries are their own trace via the immutable
            append-only ledger itself — <code>event_type</code> tags entry kind (PO_SUBMITTED,
            PO_PAID, etc.); <code>notes</code> captures contextual annotation; <code>created_at</code> +{' '}
            <code>created_by</code> identify when/who; <code>balance_after</code> captures
            running state at allocation moment
          </li>
        </ul>
      </LayerSection>

      <LayerSection layer={8} title="Propagation">
        <p>Curated propagation map (verified as of 2026-05-27 per scope §8 staleness marker):</p>
        <ul className="list-disc pl-5 text-xs space-y-2">
          {CREDIT_GATE_PROPAGATION.map((entry) => (
            <li key={entry.field}>
              <strong>
                <code>{entry.field}</code>
              </strong>{' '}
              edits affect:
              <ul className="list-circle pl-5 mt-1 space-y-0.5">
                {entry.affects.map((eff) => (
                  <li key={eff}>{eff}</li>
                ))}
              </ul>
            </li>
          ))}
        </ul>
      </LayerSection>

      <LayerSection layer={9} title="Reversibility">
        <p>
          Reversible-with-residue per scope §6.6.2 L9. Reversible going forward.{' '}
          <code>credit_ledger</code> immutability preserves prior credit-allocation decisions
          as documented at the moment of allocation (append-only; entries are not deleted or
          modified). Per-field reversibility:
        </p>
        <ul className="list-disc pl-5 text-xs space-y-0.5">
          <li>
            <code>credit_limit</code> —{' '}
            <span className="font-mono">{distFields.credit_limit.layer9Reversibility}</span>
          </li>
          <li>
            <code>payment_terms</code> —{' '}
            <span className="font-mono">{distFields.payment_terms.layer9Reversibility}</span>
          </li>
        </ul>
      </LayerSection>

      <ComposabilityReservedLayer />

      <LayerSection layer={11} title="Positional contribution">
        <p>
          Substantive-contribution per scope §6.6.2 L11. Surfaces in title block sentence 2:
          &quot;Brooklyn Low Voltage Supply on net-30 terms&quot; credit posture (Atlas Day 1
          D3 substrate-driven render).
        </p>
      </LayerSection>

      <LayerSection layer={4 /* cross-links footer */} title="Cross-links">
        <ul className="list-disc pl-5 text-xs">
          <li>
            <a href={URL_DIRECTORY_DISTRIBUTORS} className="text-accent hover:underline">
              Directory → Distributors
            </a>{' '}
            (per-distributor credit_limit + payment_terms edit surface)
          </li>
          <li>
            <a href={FINANCE_CREDIT_CASH_URL} className="text-accent hover:underline">
              Finance → Credit &amp; Cash
            </a>{' '}
            (credit-utilization dashboard + ledger entries)
          </li>
        </ul>
      </LayerSection>
    </InspectShell>
  );
}
